import React from 'react';

import useStore from 'Hooks/useStore';
import { useIsTrailerSpaceExceeded } from 'Modules/MultiStopWizard/Hooks/useIsTrailerSpaceExceeded';
import { isTripClosedByBookingType } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { LegOptionContext, LegOptionContextProps } from './Contexts/LegOptionContext';
import WaitlistButton from './WaitlistButton';

export default function LegOptionWaitlist() {
	const context = React.useContext(LegOptionContext);

	if (!context) {
		console.error('LegOptionContext is missing in LegOptionWeb');
		return null;
	}

	return <Body context={context} />;
}

function Body({ context }: { context: LegOptionContextProps }) {
	const store = useStore();

	const { trip, wizardData, isWaitlisted, refreshTrips } = context;

	const isTrailerSpaceExceeded = useIsTrailerSpaceExceeded(trip.trailerSpotsAvailable, wizardData);

	if (store.isStaff) {
		return <></>;
	}

	/*
		Closed beats full.

		A waitlist answers "tell me if space opens", and on a sailing that is shut to online bookings
		the answer would never be actionable — space could open and the customer still could not take
		it. So a trip that is BOTH closed and sold out says `CLOSED` and offers no button, which is
		also the order `useLegErrorMessage` decides the message in.

		Asked of the trip as well as of the message. The message is the thing a reader sees, but it
		is a string — reworded or translated it stops containing `CLOSED`, and the button would come
		back on exactly the sailings it must not appear on.
	*/
	if (context.errorMessage?.includes('CLOSED') || isTripClosedByBookingType(trip, wizardData.tabSelected)) {
		return <></>;
	}
	if (!context.errorMessage && !isTrailerSpaceExceeded) {
		// No need to show if no error message
		return <></>;
	}

	if (context.errorMessage?.includes('This time is')) {
		// Hide when error message is about ticket being before/after return/departure date
		return <></>;
	}

	if (context.errorMessage?.includes('Cannot book on same')) {
		// Hide when error message is about ticket being choosing the same ticket as departure/return
		return <></>;
	}

	if (trip.departed || context.availabilityStatus === 'departed') {
		// Hide when trip already departed
		return <></>;
	}

	// Only visitor/customer users can see the waitlist
	return <WaitlistButton isWaitlisted={isWaitlisted} trip={trip} wizardData={wizardData} refresh={refreshTrips} />;
}
